import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown, DropdownProps } from 'antd';
import { GenericConfirmationModal, GenericInputModal } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useAppDispatch } from '../../../store';
import { icons, IconType } from '../../icons/icons';
import MoreIconButton from '../../buttons/MoreIconButton';
import { useDeleteYbotConversationMutation, useRenameYbotConversationMutation } from './apiYbot';

enum ConversationAction {
  RENAME = 'rename',
  DELETE = 'delete',
}

const dropdownAlign: DropdownProps['align'] = { offset: [0, -6] };

interface YbotConversationMenuProps {
  conversationId: string;
  projectShortName: string;
  title: string;
  /** Called after a delete, so the dock can stop showing a conversation that is gone. */
  onDeleted: (conversationId: string) => void;
}

const YbotConversationMenu: React.FC<YbotConversationMenuProps> = ({
  conversationId,
  projectShortName,
  title,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();
  const [openAction, setOpenAction] = useState<ConversationAction | undefined>(undefined);

  const [rename] = useRenameYbotConversationMutation();
  const [remove] = useDeleteYbotConversationMutation();

  const closeModal = useCallback(() => setOpenAction(undefined), []);

  const handleRenamed = useCallback(
    (value: string) => {
      setOpenAction(undefined);
      rename({ conversationId, projectShortName, title: value.trim() })
        .unwrap()
        .catch(() => {
          // Without this the list refetches and silently restores the old name.
          dispatch(
            addToast({
              message: 'The conversation was not renamed',
              description: 'Its name is unchanged. Try again.',
              isError: true,
            }),
          );
        });
    },
    [conversationId, dispatch, projectShortName, rename],
  );

  const handleDeleted = useCallback(() => {
    setOpenAction(undefined);
    remove({ conversationId, projectShortName })
      .unwrap()
      .then(() => onDeleted(conversationId))
      .catch(() => {
        dispatch(
          addToast({
            message: 'The conversation was not deleted',
            description: 'It is still there. Try again.',
            isError: true,
          }),
        );
      });
  }, [conversationId, dispatch, onDeleted, projectShortName, remove]);

  const menu: DropdownProps['menu'] = useMemo(
    () => ({
      items: [
        {
          label: 'Rename',
          key: 'rename',
          icon: icons[IconType.EDIT_OUTLINED],
          onClick: () => setOpenAction(ConversationAction.RENAME),
        },
        { type: 'divider' as const },
        {
          label: 'Delete',
          key: 'delete',
          icon: icons[IconType.DELETE_OUTLINED],
          onClick: () => setOpenAction(ConversationAction.DELETE),
          danger: true,
        },
      ],
    }),
    [],
  );

  return (
    <>
      {/* The row itself selects the conversation, so the menu must not pass its clicks on. */}
      <span
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
        role="presentation"
      >
        <Dropdown trigger={['click']} menu={menu} align={dropdownAlign}>
          <div>
            <MoreIconButton size="xSmall" />
          </div>
        </Dropdown>
      </span>

      {openAction === ConversationAction.RENAME && (
        <GenericInputModal
          title="Rename conversation"
          placeholder="Conversation name"
          defaultValue={title}
          submitButtonLabel="Rename"
          requiredMessage="A conversation name cannot be empty."
          onConfirmed={handleRenamed}
          onClose={closeModal}
        />
      )}

      {openAction === ConversationAction.DELETE && (
        <GenericConfirmationModal
          open={true}
          title="Delete conversation"
          message={`"${title}" and everything in it will be deleted. This cannot be undone.`}
          confirmationButtonLabel="Delete"
          onConfirmed={handleDeleted}
          onCanceled={closeModal}
        />
      )}
    </>
  );
};

export default YbotConversationMenu;
